import { BILLING_TYPES } from "@/pages/quotes/detail/BillingDataModal";

/**
 * Adapter between the legacy `users-address` (Magento `customer_address_entity`)
 * shape that the Agenda UI consumes and the new `/v1/addresses` API
 * (`xconsadmindb.user_addresses`).
 *
 * Why an adapter rather than a full UI migration: the agenda render in
 * `agenda.tsx` and the form components (`DeliveryAgenda`, `BillingAgenda`)
 * already work against the legacy shape. By translating at the network
 * boundary we avoid touching the form schemas and the card layout, while
 * still routing reads/writes to the new table where snapshots and the
 * `copyFromAgenda` flow already live.
 *
 * Schema deltas:
 *   - id field renamed: userAddressId ↔ id
 *   - phone:           telephone        ↔ phone
 *   - address line:    street (string)  ↔ street1 + street2 (parsed via
 *                                        " — " separator that the legacy
 *                                        DeliveryAddressModal addToAgenda
 *                                        join uses)
 *   - region:          region           ↔ provinceName
 *   - reference:       shippingIndication ↔ reference
 *   - geo:             latitude/longitude ↔ lat/lng
 *   - store:           stores.code      ↔ store
 *   - type model:      addressType: { shipping, billing } (one row could
 *                      represent both) ↔ a single `type: 'shipping'|'billing'`
 *                      per row. We surface it back as the legacy bag with
 *                      one flag set so the agenda group-by still works.
 *   - billing type:    { label, value } ↔ tinyint id only. Label is looked
 *                      up locally from BILLING_TYPES (defined in
 *                      BillingDataModal) so the agenda card keeps showing
 *                      the human-readable label.
 */

const STREET_SEP = " — ";

const billingLabelById = (id: number | null | undefined): string | undefined => {
  if (id === null || id === undefined) return undefined;
  const idStr = String(id);
  return BILLING_TYPES.find((b) => String(b.value) === idStr)?.label;
};

const splitStreet = (
  street?: string | null,
): { street1: string; street2: string | null } => {
  if (!street) return { street1: "", street2: null };
  const trimmed = String(street).trim();
  if (!trimmed) return { street1: "", street2: null };
  const sepIdx = trimmed.indexOf(STREET_SEP);
  if (sepIdx === -1) return { street1: trimmed, street2: null };
  return {
    street1: trimmed.slice(0, sepIdx).trim(),
    street2: trimmed.slice(sepIdx + STREET_SEP.length).trim() || null,
  };
};

const joinStreet = (street1?: string | null, street2?: string | null): string => {
  const a = (street1 ?? "").trim();
  const b = (street2 ?? "").trim();
  if (a && b) return `${a}${STREET_SEP}${b}`;
  return a || b;
};

/** Map a `/v1/addresses` row → legacy `UserAddress` shape used by the agenda UI. */
export function fromV1(api: any): any {
  if (!api) return api;
  const billingTypeId =
    api.billingType !== undefined && api.billingType !== null
      ? Number(api.billingType)
      : null;
  return {
    userAddressId: api.id,
    firstname: api.firstname ?? "",
    lastname: api.lastname ?? "",
    telephone: api.phone ?? "",
    street: joinStreet(api.street1, api.street2),
    city: api.cityName ?? "",
    region: api.provinceName ?? "",
    postcode: api.postcode ?? "",
    countryId: api.countryId ?? "",
    shippingIndication: api.reference ?? "",
    vatId: api.vatId ?? "",
    company: api.company ?? "",
    latitude: api.lat ?? "",
    longitude: api.lng ?? "",
    billingTypeId,
    billingType:
      billingTypeId !== null
        ? { value: billingTypeId, label: billingLabelById(billingTypeId) ?? "" }
        : undefined,
    stores: api.store ? { code: api.store } : undefined,
    addressType: {
      shipping: api.type === "shipping",
      billing: api.type === "billing",
    },
  };
}

/**
 * Map a legacy update payload → `/v1/addresses` PATCH body. Skips undefined
 * fields so the PATCH stays partial.
 */
export function toV1Update(legacy: any): Record<string, any> {
  const out: Record<string, any> = {};
  if (legacy?.firstname !== undefined) out.firstname = legacy.firstname;
  if (legacy?.lastname !== undefined) out.lastname = legacy.lastname;
  if (legacy?.telephone !== undefined) out.phone = legacy.telephone;
  if (legacy?.street !== undefined) {
    const { street1, street2 } = splitStreet(legacy.street);
    out.street1 = street1;
    out.street2 = street2;
  }
  if (legacy?.city !== undefined) out.cityName = legacy.city;
  if (legacy?.region !== undefined) out.provinceName = legacy.region;
  if (legacy?.postcode !== undefined) out.postcode = legacy.postcode;
  if (legacy?.countryId !== undefined) out.countryId = legacy.countryId;
  if (legacy?.shippingIndication !== undefined)
    out.reference = legacy.shippingIndication;
  if (legacy?.vatId !== undefined) out.vatId = legacy.vatId;
  if (legacy?.company !== undefined) out.company = legacy.company;
  if (legacy?.latitude !== undefined)
    out.lat = legacy.latitude !== "" ? legacy.latitude : null;
  if (legacy?.longitude !== undefined)
    out.lng = legacy.longitude !== "" ? legacy.longitude : null;
  if (legacy?.billingTypeId !== undefined)
    out.billingType =
      legacy.billingTypeId === null || legacy.billingTypeId === ""
        ? null
        : Number(legacy.billingTypeId);
  return out;
}

/**
 * Map a legacy create payload → `/v1/addresses` POST body. Always emits the
 * required fields (`type`, `userId`, `firstname`, `lastname`, `street1`,
 * `postcode`) even if absent in the input, defaulting to empty strings so
 * the API contract is satisfied (DTO marks them as optional but the entity
 * column is NOT NULL).
 */
export function toV1Create(
  legacy: any,
  type: "shipping" | "billing",
  userId: number,
  store?: string | null,
): Record<string, any> {
  const base = toV1Update(legacy);
  return {
    ...base,
    type,
    userId,
    store: store ?? null,
    firstname: base.firstname ?? legacy?.firstname ?? "",
    lastname: base.lastname ?? legacy?.lastname ?? "",
    street1: base.street1 ?? "",
    street2: base.street2 ?? null,
    postcode: base.postcode ?? "",
    countryId: base.countryId ?? legacy?.countryId ?? "AR",
  };
}
